import { css } from '@emotion/react';
import React, { ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';

/** Space between the anchor and the bubble, which the arrow spans. */
const ANCHOR_GAP = 9;
const ARROW_SIZE = 6;
/** How far in from the bubble's aligned edge the arrow sits when the anchor is wider than the bubble. */
const ARROW_INSET = 32;
const VIEWPORT_MARGIN = 8;
const SHOW_DELAY_MS = 300;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** The standard bubble's widest; a tooltip holding more (a table, say) widens itself with `maxWidth`. */
const TOOLTIP_MAX_WIDTH = 393;

const TOOLTIP_BUBBLE = css`
  label: TOOLTIP_BUBBLE;
  position: fixed;
  z-index: 100000;
  box-sizing: border-box;
  width: max-content;
  max-width: ${TOOLTIP_MAX_WIDTH}px;
  padding: ${LAYOUT2.spacing1}px 13px;
  background: ${COLORS.ink};
  color: ${COLORS.paper};
  font-family: ${FONT.body};
  font-size: ${pxToRem(11.5)};
  line-height: 1.5;
  text-align: left;
  white-space: pre-line;
  overflow-wrap: break-word;
  pointer-events: none;
  transition: opacity 0.1s;
`;

const TOOLTIP_ARROW = css`
  label: TOOLTIP_ARROW;
  position: absolute;
  border-left: ${ARROW_SIZE}px solid transparent;
  border-right: ${ARROW_SIZE}px solid transparent;
`;

const TOOLTIP_ARROW_UP = css`
  label: TOOLTIP_ARROW_UP;
  top: -${ARROW_SIZE}px;
  border-bottom: ${ARROW_SIZE}px solid ${COLORS.ink};
`;

const TOOLTIP_ARROW_DOWN = css`
  label: TOOLTIP_ARROW_DOWN;
  bottom: -${ARROW_SIZE}px;
  border-top: ${ARROW_SIZE}px solid ${COLORS.ink};
`;

/** A tooltip's title: its first line, bold and white, at the body's size. */
const TOOLTIP_TITLE = css`
  label: TOOLTIP_TITLE;
  display: block;
  margin-bottom: 3px;
  color: ${COLORS.white};
  font-weight: 700;
`;

/** A tooltip's secondary paragraph (how to change what it explains, say): under the main text, smaller and faded. */
export const TOOLTIP_SECONDARY_TEXT = css`
  label: TOOLTIP_SECONDARY_TEXT;
  display: block;
  margin: 3px 0 0;
  color: ${withOpacity(COLORS.paper, 0.58)};
  font-size: ${pxToRem(10.5)};
`;

type BubblePosition = { top: number; left: number; arrowLeft: number; below: boolean };

/**
 * The bubble, under its anchor with the arrow pointing up at it; above instead when there's no room
 * below. It lines up with the anchor's edge nearer the middle of the viewport (the right edge of a
 * field on the right, the left edge of a tab on the left), so it opens inward, and stays in view.
 */
const TooltipBubble = ({ tooltipId, anchor, maxWidth, children }: {
  tooltipId: string;
  anchor: HTMLElement;
  maxWidth?: number;
  children: ReactNode;
}) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<BubblePosition>();

  useLayoutEffect(() => {
    const bubble = bubbleRef.current!.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const fitsBelow = anchorRect.bottom + ANCHOR_GAP + bubble.height <= window.innerHeight - VIEWPORT_MARGIN;
    const below = fitsBelow || anchorRect.top - ANCHOR_GAP - bubble.height < VIEWPORT_MARGIN;
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const alignLeft = anchorCenter < window.innerWidth / 2;
    const left = clamp(
      alignLeft ? anchorRect.left : anchorRect.right - bubble.width,
      VIEWPORT_MARGIN,
      window.innerWidth - VIEWPORT_MARGIN - bubble.width,
    );
    const widestArrowLeft = bubble.width - 3 * ARROW_SIZE;
    setPosition({
      top: below ? anchorRect.bottom + ANCHOR_GAP : anchorRect.top - ANCHOR_GAP - bubble.height,
      left,
      arrowLeft: anchorRect.width > bubble.width
        ? alignLeft ? ARROW_INSET : widestArrowLeft + ARROW_SIZE - ARROW_INSET
        : clamp(anchorCenter - left - ARROW_SIZE, ARROW_SIZE, widestArrowLeft),
      below,
    });
  }, [anchor, children]);

  return createPortal(
    <div
      ref={bubbleRef}
      id={tooltipId}
      role='tooltip'
      css={TOOLTIP_BUBBLE}
      // measured unseen first, then faded in where it belongs
      style={{ maxWidth, ...position ? { top: position.top, left: position.left, opacity: 1 } : { top: 0, left: 0, opacity: 0 } }}
    >
      {children}
      {position && <span css={[TOOLTIP_ARROW, position.below ? TOOLTIP_ARROW_UP : TOOLTIP_ARROW_DOWN]} style={{ left: position.arrowLeft }} />}
    </div>,
    document.body,
  );
};

/**
 * A styled tooltip for any element, headed by `title` when given one: spread `anchorProps` onto the
 * element it explains and render `tooltip` anywhere (it portals to the body). It shows on hover, or on keyboard focus of the anchor
 * itself, after a short delay. Tooltips explain; they never hold controls, since the pointer can't reach them.
 *
 * Pressing within the anchor dismisses it until the pointer leaves, as native tooltips do: once the
 * user acts (clicks, starts typing), the explanation would only cover what they're doing.
 */
export const useTooltip = (
  content: ReactNode,
  { title, maxWidth }: { title?: string; /** Wider than the standard TOOLTIP_MAX_WIDTH, for a table, say. */ maxWidth?: number } = {},
) => {
  const tooltipId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [delayElapsed, setDelayElapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDelayElapsed(false);
    if (!anchor) return;
    const timeout = setTimeout(() => setDelayElapsed(true), SHOW_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [anchor]);

  const show = (event: React.SyntheticEvent<HTMLElement>) => {
    setDismissed(false);
    setAnchor(event.currentTarget);
  };
  const hide = () => setAnchor(null);
  const visible = !!content && !!anchor && delayElapsed && !dismissed;

  return {
    anchorProps: {
      onMouseEnter: show,
      onMouseLeave: hide,
      onPointerDown: () => setDismissed(true),
      onKeyDown: () => setDismissed(true),
      // focus bubbles in React: only the anchor's own focus shows it, not a control's inside it; the
      // focus a click brings keeps the click's dismissal, which lasts until focus leaves
      onFocus: (event: React.FocusEvent<HTMLElement>) => event.target === event.currentTarget && setAnchor(event.currentTarget),
      onBlur: (event: React.FocusEvent<HTMLElement>) => {
        if (event.target !== event.currentTarget) return;
        setDismissed(false);
        hide();
      },
      'aria-describedby': visible ? tooltipId : undefined,
    },
    tooltip: visible
      ? (
        <TooltipBubble tooltipId={tooltipId} anchor={anchor} maxWidth={maxWidth}>
          {title && <span css={TOOLTIP_TITLE}>{title}</span>}
          {content}
        </TooltipBubble>
      )
      : null,
  };
};
